// @/shared/stores/useOtherValuesStore.ts
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { OtherProps } from "@/shared/types/Form";

interface OtherValuesState {
    // Core state
    otherValuesMap: Record<string, OtherProps>;

    // Actions
    updateOtherValues: (stepId: string, newValues: OtherProps) => void;
    removeOtherValues: (stepId: string) => void;
    clearAllOtherValues: () => void;
    getOtherValues: (stepId: string) => OtherProps | undefined;
}

/**
 * Other Values Store Factory - Form Field Persistence
 *
 * Creates a form-specific store that manages otherValues (like membershipId) that need to be:
 * - Validated as part of the form
 * - Persisted locally (not sent to quote API)
 * - Restored when user returns to form steps
 * - Isolated per form ID
 *
 * Key features:
 * - Automatic localStorage persistence with form-specific keys
 * - Step-based organization of otherValues
 * - Immutable updates via immer
 * - Form isolation prevents cross-form contamination
 */

const createOtherValuesStore = (formId: string) => {
    return create<OtherValuesState>()(
        persist(
            immer((set, get) => ({
                otherValuesMap: {},

                updateOtherValues: (stepId: string, newValues: OtherProps) => {
                    if (!stepId || !newValues || Object.keys(newValues).length === 0) return;

                    set(state => {
                        // Merge new values with existing ones for this step
                        state.otherValuesMap[stepId] = {
                            ...state.otherValuesMap[stepId],
                            ...newValues
                        };
                    });
                },

                removeOtherValues: (stepId: string) => {
                    if (!stepId) return;

                    set(state => {
                        delete state.otherValuesMap[stepId];
                    });
                },

                clearAllOtherValues: () => {
                    set(state => {
                        state.otherValuesMap = {};
                    });
                },

                getOtherValues: (stepId: string) => {
                    return get().otherValuesMap[stepId];
                }
            })),
            {
                name: `${formId}-other-values`, // Dynamic localStorage key
                storage: createJSONStorage(() => localStorage),
                // Only persist the otherValuesMap, not the action functions
                partialize: state => ({ otherValuesMap: state.otherValuesMap })
            }
        )
    );
};

// Store instances cache to avoid creating multiple stores for the same form
const storeCache = new Map<string, ReturnType<typeof createOtherValuesStore>>();

// Returns the bound zustand store hook for a form, creating it on first use. Not a React hook itself
// (no hook calls, module-level cache), so it is deliberately NOT named `use*`: the React Compiler
// treats `use`-prefixed functions as hooks and everything else as memoizable plain calls.
export const getOtherValuesStore = (formId: string) => {
    // Get or create store instance for this formId
    if (!storeCache.has(formId)) {
        storeCache.set(formId, createOtherValuesStore(formId));
    }

    return storeCache.get(formId)!;
};

// Selective subscriptions for performance
// The bound store is a React hook (zustand's useStore). It MUST be called through a `use*`
// binding: with `const store = ...; store(selector)` the React Compiler saw a plain call and
// memoized its result, so on re-render the hook was skipped, the component's hook order shifted,
// and the quote forms crashed on mount ("Cannot read properties of undefined (reading 'length')").
export const useOtherValuesMap = (formId: string) => {
    const useStore = getOtherValuesStore(formId);
    return useStore(state => state.otherValuesMap);
};

export const useOtherValuesActions = (formId: string) => {
    const useStore = getOtherValuesStore(formId);
    return useStore(state => ({
        updateOtherValues: state.updateOtherValues,
        removeOtherValues: state.removeOtherValues,
        clearAllOtherValues: state.clearAllOtherValues,
        getOtherValues: state.getOtherValues
    }));
};

// Helper for getting specific step values
export const useStepOtherValues = (formId: string, stepId: string) => {
    const useStore = getOtherValuesStore(formId);
    return useStore(state => state.otherValuesMap[stepId]);
};
